/**
 * ManagedInBrandBible — a quiet signpost for settings concepts owned by the
 * Brand Bible. Values remain visible here for evaluation, but editing always
 * happens in the named Brand Bible section.
 */

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Lock } from 'lucide-react';

import { COPY } from '@/lib/copyTokens';

export interface BrandBibleDeferralItem {
  concept: string;
  value: string | string[] | null | undefined;
  section: string;
  to?: string;
}

export interface ManagedInBrandBibleProps {
  header: string;
  items: BrandBibleDeferralItem[];
}

function isEmptyValue(value: BrandBibleDeferralItem['value']) {
  return value == null || value === '' || (Array.isArray(value) && value.length === 0);
}

function BrandBibleValue({ value }: Pick<BrandBibleDeferralItem, 'value'>) {
  if (isEmptyValue(value)) {
    return <span className="text-[14px] text-ink-2">{COPY.brandBible.deferralEmpty}</span>;
  }

  if (Array.isArray(value)) {
    return (
      <span className="flex flex-wrap gap-1.5">
        {value.map((entry, index) => (
          <span
            key={`${entry}-${index}`}
            className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
          >
            {entry}
          </span>
        ))}
      </span>
    );
  }

  return <span className="text-[14px] text-ink-2">{value}</span>;
}

export function ManagedInBrandBible({ header, items }: ManagedInBrandBibleProps): JSX.Element {
  return (
    <section className="rounded-2xl border border-border bg-card px-6 py-5 shadow-sm">
      <header className="flex items-center gap-3">
        <span className="grid size-9 place-items-center rounded-xl border border-border bg-warm-bg text-muted-foreground">
          <BookOpen aria-hidden="true" className="size-4" />
        </span>
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {header}
        </h2>
      </header>

      <div className="mt-4">
        {items.map((item, index) => (
          <div
            key={`${item.concept}-${item.section}`}
            className={`grid gap-3 py-4 md:grid-cols-[minmax(9rem,0.8fr)_minmax(0,1.5fr)_auto] md:items-center${
              index > 0 ? ' border-t border-border' : ''
            }`}
          >
            <p className="text-[12.5px] font-semibold text-muted-foreground">{item.concept}</p>
            <div className="flex flex-wrap items-center gap-2">
              <BrandBibleValue value={item.value} />
              <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                <Lock aria-hidden="true" className="size-3.5" />
                {COPY.brandBible.deferralBadge}
              </span>
            </div>
            <Link
              to={item.to ?? '/brand-bible'}
              className="text-sm text-ink-2 hover:text-ink md:justify-self-end md:text-right"
            >
              {COPY.brandBible.editLink(item.section)}
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}
